import { PhoneNumberValue } from '@/components/form/PhoneNumberField';
import { normalizePhoneNumber, PhoneRegion } from '@/helpers/phoneHelper';
import { handleMutationResult } from '@/helpers/toastHelper';
import { useDefaultPhoneRegion } from '@/services/phone/useDefaultPhoneRegion';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { TsForm } from '../../../../components/form/Form';
import { Button } from '../../../../components/shared/button/Button';
import { TSCard } from '../../../../components/shared/card/Card';
import { Role } from '../../../../services/authentication/authentication-service';
import { CreateEmployee, useCreateMultipleUsersAndEmployeesMutation } from '../../../../services/gql/graphql.generated';
import { useLocationService } from '../../../../services/location/LocationService';
import strings from '../../team.translations';
import { AddTeamMemberRow } from './AddTeamMemberRow';

export interface RoleOption {
  id: Role;
  name: string;
}

interface MemberFields {
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: PhoneNumberValue;
  role: RoleOption;
  sendRegistration: boolean;
  userName: string;
  locationIds?: number[];
}

const buildNewMember = (defaultRegion: PhoneRegion): MemberFields => ({
  firstName: '',
  lastName: '',
  role: { id: Role.Staff, name: strings.common.roles.staff },
  sendRegistration: true,
  userName: '',
  phoneNumber: { number: '', region: defaultRegion }
});

const toPayload = (member: MemberFields, locationId: number | undefined): CreateEmployee => ({
  firstName: member.firstName,
  lastName: member.lastName,
  role: member.role?.id ?? '',
  userName: uuidv4(),
  sendRegistration: member.sendRegistration,
  ...(locationId && { locationIds: [locationId] }),
  ...(member.email && { email: member.email }),
  ...(member.phoneNumber?.number && {
    phoneNumber: normalizePhoneNumber(member.phoneNumber.number, member.phoneNumber.region)
  })
});

export const AddMultipleTeamMembersForm: FC = () => {
  const locationId = useLocationService(state => state.selectedLocation?.id);
  const navigate = useNavigate();
  const [{ fetching }, createMultipleUsersAndEmployees] = useCreateMultipleUsersAndEmployeesMutation();

  const defaultRegion = useDefaultPhoneRegion();

  const methods = useForm<{ members: MemberFields[] }>({
    defaultValues: { members: [buildNewMember(defaultRegion)] }
  });

  const memberList = methods.watch('members');

  const handleAdd = (i: number) => () => {
    memberList.splice(i + 1, 0, buildNewMember(defaultRegion));
    methods.setValue('members', memberList);
  };

  const handleDelete = (i: number) => () => {
    if (memberList.length > 1) {
      memberList.splice(i, 1);
      methods.setValue('members', memberList);
    }
  };

  const handleSubmit = async (data: { members: MemberFields[] }) => {
    const result = await createMultipleUsersAndEmployees({
      employees: data.members.map(m => toPayload(m, locationId))
    });

    await handleMutationResult(result, {
      successMessage: 'Team members successfully added.',
      errorMessage: 'Unable to add team members.',
      onSuccess: () => navigate('/team')
    });
  };

  return (
    <TsForm methods={methods} onSubmit={handleSubmit} className="flex flex-col gap-5">
      <TSCard>
        <div className="w-full overflow-x-hidden">
          <div className="overflow-auto p-5">
            <div
              style={{ gridTemplateColumns: '3fr 3fr 5fr 1fr 1fr 80px' }}
              className="grid grid-cols-6 gap-x-5 gap-y-2 min-w-[800px]"
            >
              <label className="text-sm">{strings.addTeamMemberForm.firstName}</label>
              <label className="text-sm">{strings.addTeamMemberForm.lastName}</label>
              <label className="text-sm">{strings.addTeamMemberForm.contactInformation}</label>
              <label className="text-sm">{strings.addTeamMemberForm.role}</label>
              <label className="text-sm text-center">{strings.teamMembersModal.invite}</label>
              <div />
              {memberList.map((_, idx) => (
                <AddTeamMemberRow
                  key={idx}
                  methods={methods}
                  idx={idx}
                  handleDelete={handleDelete(idx)}
                  handleAdd={handleAdd(idx)}
                />
              ))}
            </div>
          </div>
        </div>
      </TSCard>
      <div className="justify-end gap-5 flex">
        <Button type="button" onClick={() => navigate('/team')}>
          {strings.common.cancel}
        </Button>
        <Button primary type="submit" loading={fetching}>
          {strings.teamMembersModal.submitButton}
        </Button>
      </div>
    </TsForm>
  );
};
